import { findCountryByIso2 } from '@/utils/find-country-by-iso2';

export interface AddressParts {
  country?: string | null;
  city?: string | null;
  street?: string | null;
  street2?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

const PART_ORDER: Array<keyof AddressParts> = [
  'street',
  'street2',
  'city',
  'state',
  'postalCode',
  'country',
];

const isAddressParts = (parsed: unknown): parsed is AddressParts =>
  typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);

export const formatContactAddressParts = (parts: AddressParts): string =>
  PART_ORDER.map((part) => {
    const raw = parts[part];
    if (typeof raw !== 'string' || !raw.trim()) return null;
    const trimmed = raw.trim();
    return part === 'country' ? (findCountryByIso2(trimmed)?.name ?? trimmed) : trimmed;
  })
    .filter(Boolean)
    .join(', ');

/**
 * Splits a multi-line street (business-card extraction) into Address 1 + Address 2, mirroring the
 * API's own fold: first line becomes the street, the rest join with ', ' into the second line.
 */
export const splitStreetLines = (
  raw: string | null | undefined,
): { street: string | null; street2: string | null } => {
  const lines = (raw ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return { street: lines[0] ?? null, street2: lines.slice(1).join(', ') || null };
};

export const formatContactAddress = (value: string | null | undefined): string => {
  if (!value) return '';

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value.trim();
  }

  if (!isAddressParts(parsed)) return value.trim();

  return formatContactAddressParts(parsed) || value.trim();
};
